import {
  AfterViewInit,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RoutePlannerAutoCompleteData } from '../address-auto-complete.component';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { Overlay } from '@angular/cdk/overlay';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import {
  MapService,
  MarkersClustering,
  MarkerTrackingOptions,
} from 'src/app/modules/route-planner/services/map/map.service';
import {
  AddressAutocompleteMarkerComponent,
  AddressMarkerData,
} from './address-autocomplete-marker/address-autocomplete-marker.component';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';
import { BaseMap } from 'src/app/modules/route-planner/components/shared/map/base-map.component';
import { MAP_ICONS } from 'src/app/modules/route-planner/constants/map-icons.constants';
import {
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  fromEvent,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { MapLocations } from 'src/app/modules/route-planner/interfaces/location/map-locations.interface';
import { MapComponent } from 'src/app/modules/route-planner/components/shared/map/map.component';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  PARKING_LOTS,
  PICK_MAP_LOCATION_TEXT,
  PIN_INSERTION,
  PUBLIC_TRANSPORTATION_STATIONS,
  RAV_KAV_CHARGING_STATIONS,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { Direction } from '@angular/cdk/bidi';
import { StationsAutoCompleteMapMarkerComponent } from
  '../../../search-station-form/stations-auto-complete/stations-auto-complete-map/stations-auto-complete-map-marker/stations-auto-complete-map-marker.component';
import { eMapLocationType } from 'src/app/modules/route-planner/enums/route-planning/location-type.enum';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';

import { RavKavStation } from 'src/app/modules/route-planner/interfaces/location/ravkav-station.interface';
import { ParkAndRide } from 'src/app/modules/route-planner/interfaces/location/park-and-ride.interface';
import { StopDetails } from 'src/app/modules/route-planner/interfaces/location/stop-details.interface';

type LayerKind = 'PARKING' | 'RAVKAV' | 'STOP';

@Component({
  selector: 'app-address-auto-complete-map',
  templateUrl: './address-auto-complete-map.component.html',
  styleUrls: ['./address-auto-complete-map.component.scss'],
  providers: [MapService],
  standalone: false
})
export class AddressAutoCompleteMapComponent
  extends BaseMap<Address>
  implements OnInit, AfterViewInit {

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(MapComponent)
  override mapComponent?: MapComponent;

  /**
   * Global map address search is only for מוצא–יעד / רכב משולב. On other tabs it sat next in tab order
   * and stole focus (and focus rings) from the drawer form.
   */
  mapAddressSearchTabIndex = 0;

  pickMapLocationText: string
  direction: Direction
  ignoreMapTap: boolean;
  ravkavStations: RavKavStation[];
  parkAndGoLots: ParkAndRide[];
  stops: StopDetails[];
  addressMarkersGroup?: H.map.Group;

  pinInsertionText: string;

  constructor(
    favoritesService: FavoritesService,
    resizeService: ResizeService,
    overlay: Overlay,
    protected override readonly routePlanningService: RoutePlanningService,
    protected override readonly mapService: MapService,
    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: RoutePlannerAutoCompleteData,
    private readonly locationService: LocationService,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly languageService: LanguageService,
    private readonly queryParamsService: QueryParamsService,
    private viewContainerRef: ViewContainerRef,
  ) {
    super(
      favoritesService,
      resizeService,
      overlay,
      mapService,
      routePlanningService
    );

    this.mapService.setViewContainerRef(this.viewContainerRef);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.data?.initialValue) {
      this.formControl.setValue(this.data.initialValue);
    }

    this.formControl.valueChanges
      .pipe(
        debounceTime(AutoCompleteService.AUTO_COMPLETE_DEBOUNCE_TIME),
        distinctUntilChanged(),
        switchMap((val) => this.autoCompleteService.searchLocations(val))
      )
      .subscribe((results) => (this.results$ = of(results)));

    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.pickMapLocationText = PICK_MAP_LOCATION_TEXT[language.language];
      this.pinInsertionText = PIN_INSERTION[language.language];
    });

    this.queryParamsService.queryParams$.subscribe((params) => {
      this._stopOpenFromQueryParam = !!params['stopOpen'];
    });

    this.syncMapAddressSearchTabIndex(undefined);
    this.queryParamsService._tabChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tab) => this.syncMapAddressSearchTabIndex(tab));
  }

  private syncMapAddressSearchTabIndex(tab: eTab | undefined): void {
    const t =
      tab ??
      (this.routePlanningService.getCurrentTab() as eTab | undefined);
    const allow =
      t == null ||
      t === eTab.SOURCE_DESTINATION ||
      t === eTab.CAR_INTERGRATED;
    this.mapAddressSearchTabIndex = allow ? 0 : -1;
  }

  _stopOpenFromQueryParam = false;

  override ngAfterViewInit(): void {
    if (!this.mapComponent) return;

    // Fetch data separately (stations, parking lots, and RavKav come separately)
    forkJoin({
      ravkavStations: this.locationService.getRavKavStations(),
      parkAndGoLots: this.locationService.getMapParkAndGoLots(),
      stops: this.locationService.getMapStops()
    }).subscribe({
      next: ({ ravkavStations, parkAndGoLots, stops }) => {
        // Preserve separate data storage
        this.ravkavStations = ravkavStations.data;
        this.parkAndGoLots = parkAndGoLots.data;
        this.stops = stops.data;

        // Map the data to the correct format for rendering
        const mappedData = mapLocationsToMapLocations(
          this.ravkavStations,
          this.parkAndGoLots,
          this.stops
        );

        // Initialize the map
        const ok = this.mapService.initializeMap(this.mapComponent.mapDiv, {
          markersClustering: this.getMarkersClusetring(mappedData),
          focusOnUserLocation: false,
          controls: {
            zoom: true,
            myLocation: true,
            scalebar: true,
            mapSettings: true,
          },
        });

        this._errLoadingMap = !ok;
      },
      error: (err) => {
        LogService.logError(err);
        this._errLoadingMap = true;
      },
    });

    this.mapService.$mapInitialized.subscribe((map) => {
      this._mapInitialized = true;

      map.addEventListener('tap', (ev: any) => {
        ev.preventDefault();

        // Click on marker / DomMarker / cluster - let them handle it
        if (ev.target instanceof H.map.Marker || ev.target instanceof H.map.DomMarker) {
          return;
        }

        this.handleMapTap(map, ev);
      });
    });
  }

  /** Click on empty map: green pin + reverse-geocode */
  private handleMapTap(map: H.Map, ev: any) {

    if (this.ignoreMapTap) {
      this.ignoreMapTap = false;
      return;
    }

    this.clearAddressMarkersGroup();

    const p = ev.currentPointer;
    const geo = map.screenToGeo(p.viewportX, p.viewportY);

    const coord: Coordinate = { lat: geo.lat, lng: geo.lng };

    this.addressMarkersGroup = new H.map.Group();
    const tmp = new H.map.Marker(coord, {
      data: {},
      icon: MAP_ICONS.pin_green(),
    });

    this.addressMarkersGroup.addObject(tmp);
    this.mapService.addObject(this.addressMarkersGroup);
    const address = {
      addressDetails: {
        label: this.pinInsertionText,
      },
      coordinates: coord,
    }

    this.results$ = of([{ label: address.addressDetails.label, value: address }]);
    this.formControl.setValue(address.addressDetails.label, { emitEvent: false });

    this.displayAddressMarker(address);


  }

  /** Marker factory for cluster */
  private buildNoisePoint(
    noisePoint: H.clustering.INoisePoint,
    layer: LayerKind,
  ): H.map.Marker {

    const language = this.languageService.getSelectedLanguage();
    const raw = noisePoint.getData() as any;

    // For MapLocations from API:
    // raw = { coordinates:{lat,lng}, data:{...} }
    const coords: Coordinate = raw.coordinates;
    const domain = raw.data; // RavKav | Parking | Stop

    // ----------- FIX 1: determine normalized name ----------
    let label = '';
    if (layer === 'PARKING') {
      label = domain?.name[language.symbol] || domain?.name.he || '';
    }
    else if (layer === 'RAVKAV') {
      // RavKav has no name field — use any localized variant
      label = domain?.NameOfStationEng
        || domain?.NameOfStationHeb
        || domain?.nameOfStationHeb
        || '';
    }
    else if (layer === 'STOP') {
      label = domain?.name || domain?.Name || '';
    }

    // ----------- FIX 2: proper icon --------------
    let icon: H.map.Icon;

    switch (layer) {
      case 'PARKING':
        icon = domain?.isTrainStation
          ? MAP_ICONS.park_and_ride_train()
          : MAP_ICONS.park_and_ride();
        break;
      case 'RAVKAV':
        icon = MAP_ICONS.rav_kav_station();
        break;
      default:
        icon = MAP_ICONS.station();
    }

    // ----------- FIX 3: ensure coordinates are valid ----------
    if (!coords?.lat || !coords?.lng) {
      console.warn('Invalid coordinates in cluster point:', domain);
      return new H.map.Marker({ lat: 0, lng: 0 }); // At least don't break
    }

    const marker = new H.map.Marker(coords, {
      data: { ...domain, label }, // Save label
      min: noisePoint.getMinZoom(),
      icon,
    });

    // ---------- Click event ----------
    marker.addEventListener('tap', (ev: any) => {
      ev.stopPropagation();

      if (layer === 'STOP') {
        this.displayStationMarker(coords, domain, marker);
      }
      else if (layer === 'PARKING') {
        this.displayAddressMarker({
          coordinates: coords,
          addressDetails: { label },
        });
      }
      else if (layer === 'RAVKAV') {
        this.displayRavKavStationMarker(coords, domain);
      }
    });

    return marker;
  }


  private getMarkersClusetring(data: MapLocations): MarkersClustering {
    const language = this.languageService.getSelectedLanguage();
    const options = {
      eps: 3,
      minWeight: 50,
    };
    const trackingOptions: MarkerTrackingOptions = {
      $location: this.mapService.$mapViewChanged.asObservable(),
      maxDistance: 500,
      minZoom: 14,
    };

    return {
      layers: [
        {
          label: PARKING_LOTS[language.language],
          dataPoints: data.parkings,
          dataPointObjectFactory: (noisePoint) =>
            this.buildNoisePoint(noisePoint, 'PARKING'),
          options,
          tracking: trackingOptions,
        },
        {
          label: RAV_KAV_CHARGING_STATIONS[language.language],
          dataPoints: data.ravKavStations,
          dataPointObjectFactory: (noisePoint) =>
            this.buildNoisePoint(noisePoint, 'RAVKAV'),
          options,
          tracking: trackingOptions,
        },
        {
          label: PUBLIC_TRANSPORTATION_STATIONS[language.language],
          dataPoints: data.stops,
          dataPointObjectFactory: (noisePoint) =>
            this.buildNoisePoint(noisePoint, 'STOP'),
          options,
          tracking: trackingOptions,
        },
      ],
    };
  }

  /** Station card (DomMarker + DomIcon) */
  displayStationMarker(
    coordinates: Coordinate,
    locationData: any,
    _targetMarker: H.map.Marker
  ) {
    // First remove the previous card/pin
    this.clearAddressMarkersGroup();

    const address: Address = {
      coordinates,
      addressDetails: {
        label: `${locationData.name[this.languageService.getSelectedLanguage().symbol]} - ${locationData.stopcode}`,
      },
    };

    const showAddStopButton = this._stopOpenFromQueryParam;

    const isMobile = this.resizeService.isMobile;

    const container = this.mapService.createMapObject<
      StationsAutoCompleteMapMarkerComponent,
      any
    >(StationsAutoCompleteMapMarkerComponent, { stop: locationData, showAddStopButton, showOriginDestinationButtons: true, showPickStationButton: isMobile ? true : false });

    const icon = new H.map.DomIcon(container, {
      onAttach: (el) => {
        this.attachStationMarkerListeners(el, address);
      },
    });

    const stationMarker = new H.map.DomMarker(coordinates, {
      data: {},
      icon,
    });

    this.addressMarkersGroup = new H.map.Group();
    this.addressMarkersGroup.addObject(stationMarker);
    this.mapService.addObject(this.addressMarkersGroup);
    this.mapService.updateMapView(coordinates.lat, coordinates.lng, 18);
  }

  /** RavKav station */
  displayRavKavStationMarker(
    coordinates: Coordinate,
    locationData: RavKavStation
  ) {
    const language = this.languageService.getSelectedLanguage();

    let stationName = '';
    let stationAddress = '';
    let cityName = '';

    if (language.symbol === 'en') {
      stationName = locationData.nameOfStationEng;
      stationAddress = locationData.addressEng;
      cityName = locationData.cityEng;
    } else if (language.symbol === 'ar') {
      stationName = locationData.nameOfStationArb || locationData.nameOfStationEng;
      stationAddress = locationData.addressArb || locationData.addressEng;
      cityName = locationData.cityArb || locationData.cityEng;
    } else {
      stationName = locationData.nameOfStationHeb;
      stationAddress = locationData.addressHeb;
      cityName = locationData.cityHeb;
    }

    const address: Address = {
      coordinates,
      addressDetails: {
        label: `${stationName} - ${stationAddress}, ${cityName}`,
      },
    };

    this.displayAddressMarker(address);
  }

  /** Address card (DomMarker + DomIcon) */
  displayAddressMarker(address: Address) {

    if (this.ignoreMapTap) {
      this.ignoreMapTap = false;
      return;
    }

    this.clearAddressMarkersGroup();

    const coordinates = address.coordinates;

    const pinMarker = new H.map.Marker(coordinates, {
      data: {},
      icon: MAP_ICONS.pin_green(),
    });

    const showAddStopButton = this._stopOpenFromQueryParam;
    const container = this.mapService.createMapObject<
      AddressAutocompleteMarkerComponent,
      AddressMarkerData
    >(AddressAutocompleteMarkerComponent, {
      address,
      title: this.data.title,
      showAddStopButton,
    });

    const icon = new H.map.DomIcon(container, {
      onAttach: (el) => {
        this.attachAddressMarkerListeners(el, address);
      },
    });

    const detailsMarker = new H.map.DomMarker(coordinates, {
      data: {},
      icon,
    });

    this.addressMarkersGroup = new H.map.Group();
    this.addressMarkersGroup.addObject(pinMarker);
    this.addressMarkersGroup.addObject(detailsMarker);

    this.mapService.addObject(this.addressMarkersGroup);
    this.mapService.updateMapView(coordinates.lat, coordinates.lng, 18);
  }

  private addMobileSafeListener(element: Element, handler: (e: Event) => void): void {
    let handled = false;
    const safeHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (handled) return;
      handled = true;
      handler(e);
    };
    element.addEventListener('touchend', safeHandler);
    element.addEventListener('click', safeHandler);
  }

  private attachStationMarkerListeners(el: Element, address: Address): void {

    const handleChoose = (target: 'origin' | 'destination' | 'stop') => {
      this.clearAddressMarkersGroup();
      if (this.data?.keepMapOpenOnSelect) {
        this.data?.closeCallbackFn?.(address, target);
      } else {
        this.data?.closeCallbackFn?.(address, target);
        this.closeCard(address);
      }
    };


    const bind = () => {
      const chooseOrigin = el.querySelector('.choose-origin');
      if (chooseOrigin) {
        this.addMobileSafeListener(chooseOrigin, () => handleChoose('origin'));
      }

      const chooseDestination = el.querySelector('.choose-destination');
      if (chooseDestination) {
        this.addMobileSafeListener(chooseDestination, () => handleChoose('destination'));
      }

      const chooseStop = el.querySelector('.choose-stop');
      if (chooseStop) {
        this.addMobileSafeListener(chooseStop, () => handleChoose('stop'));
      }

      const chooseStation = el.querySelector('.choose-station');
      if (chooseStation) {
        this.addMobileSafeListener(chooseStation, () => handleChoose('stop'));
      }


      const close = el.querySelector('.close-station-data');
      if (close) {
        this.addMobileSafeListener(close, () => {
          this.clearAddressMarkersGroup(true);
        });
      }
    };


    bind();

  }

  private attachAddressMarkerListeners(el: Element, address: Address): void {
    const handleChoose = (target: 'origin' | 'destination' | 'stop') => {
      this.clearAddressMarkersGroup();
      if (this.data?.keepMapOpenOnSelect) {
        this.data?.closeCallbackFn?.(address, target);
      } else {
        this.data?.closeCallbackFn?.(address, target);
        this.closeCard(address);
      }
    };

    const bind = () => {
      const chooseOrigin = el.querySelector('.choose-origin');
      if (chooseOrigin) {
        this.addMobileSafeListener(chooseOrigin, () => handleChoose('origin'));
      }

      const chooseDestination = el.querySelector('.choose-destination');
      if (chooseDestination) {
        this.addMobileSafeListener(chooseDestination, () => handleChoose('destination'));
      }

      const chooseStop = el.querySelector('.choose-stop');
      if (chooseStop) {
        this.addMobileSafeListener(chooseStop, () => handleChoose('stop'));
      }

      const chooseAddress = el.querySelector('.choose-address');
      if (chooseAddress) {
        this.addMobileSafeListener(chooseAddress, () => handleChoose('origin'));
      }

      const close = el.querySelector('.close-station-data');
      if (close) {
        this.addMobileSafeListener(close, () => {
          this.clearAddressMarkersGroup(true);
        });
      }
    };


    bind();

  }

  private clearAddressMarkersGroup(ignoreNextMapTap: boolean = false): void {
    if (this.addressMarkersGroup) {
      this.mapService.removeObject(this.addressMarkersGroup);
      this.addressMarkersGroup = undefined as any;
    }

    if (ignoreNextMapTap) {
      this.ignoreMapTap = true;
    }
  }
}

/**
 * Optimized mapping function for large datasets (10k+ items)
 * Uses pre-allocated arrays and direct property access for better performance
 *
 * @param ravkavStations - Array of RavKav charging stations
 * @param parkAndGoLots - Array of park and ride lots
 * @param stops - Array of public transportation stops
 * @returns MapLocations object with properly formatted location data
 */
function mapLocationsToMapLocations(
  ravkavStations: RavKavStation[] = [],
  parkAndGoLots: ParkAndRide[] = [],
  stops: StopDetails[] = []
): MapLocations {
  // Pre-allocate arrays for better performance with large datasets
  const ravKavStationsLength = ravkavStations.length;
  const mappedRavKavStations = new Array(ravKavStationsLength);

  // Map RavKav stations using for loop (faster than .map() for large arrays)
  for (let i = 0; i < ravKavStationsLength; i++) {
    const station = ravkavStations[i];
    mappedRavKavStations[i] = {
      type: eMapLocationType.RAV_KAV_STATION,
      coordinates: {
        lat: station.latitude,
        lng: station.longitude
      },
      data: station
    };
  }

  // Map park and ride lots
  const parkingsLength = parkAndGoLots.length;
  const mappedParkings = new Array(parkingsLength);

  for (let i = 0; i < parkingsLength; i++) {
    const parking = parkAndGoLots[i];
    mappedParkings[i] = {
      type: eMapLocationType.PARK_AND_RIDE,
      coordinates: {
        lat: parking.lat,
        lng: parking.lng
      },
      data: parking
    };
  }

  // Map stops - filter out invalid coordinates first, then map
  const stopsLength = stops.length;
  const validStops = [];

  for (let i = 0; i < stopsLength; i++) {
    const stop = stops[i];
    if (stop.lat !== undefined && stop.lng !== undefined) {
      validStops.push({
        type: eMapLocationType.STATION,
        coordinates: {
          lat: stop.lat,
          lng: stop.lng
        },
        data: stop
      });
    }
  }

  return {
    ravKavStations: mappedRavKavStations,
    parkings: mappedParkings,
    stops: validStops
  };
}
